
import { throwError as observableThrowError, Subscription, interval } from 'rxjs';

import { catchError } from 'rxjs/operators';
import { Component, OnDestroy, AfterViewInit, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BannerNotificationsService, LocalStorageService } from '../../../xform-compat';
import { TINYMCE_FONT_STYLE } from '../../constants/tinymce-content-style';
import { ConnectionService } from '../../../core/services';
import 'tinymce/tinymce.min';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/image';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/models/dom';

/**
 * Declare a variable to prevent errors
 * Because of unrecognized 3rd party type
 */
declare let tinymce: any;
declare let $: any;

/**
 * Component class for TinyMCE rich text editor
 */
@Component({
    selector: 'rms-rich-text-editor',
    template: `<textarea id="{{elementId}}" required>{{content}}</textarea>`,
    styleUrls: ['./rms-rich-text-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RmsRichTextEditorComponent implements OnDestroy, AfterViewInit {
  @Input() elementId: string;
  @Input() content: string;
  @Input() enableAutoSave: boolean;
  @Input() focusOnLoad = true;
  @Output() onEditorChange = new EventEmitter<any>();
  @Output() autoSave: EventEmitter<{content: string, isOffline: boolean}> = new EventEmitter<{content: string, isOffline: boolean}>();
  private continuousTypingSubscription: Subscription;
  private idleTypingSubscription: Subscription;
  private continuousTypingCounter = 0;
  private idleTypingCounter = 0;
  private isOffline: boolean;
  private connectionSubscription: Subscription;
  public editor: any;

  constructor (
    private bannerNotificationService: BannerNotificationsService,
    private connectionService: ConnectionService,
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  /**
   * Waits until the browser renders the component to init the editor and set the configs
   */
  ngAfterViewInit (): void {
    tinymce.init({
      license_key: 'gpl',
      selector: `#${this.elementId}`,
      branding: false,
      content_style: this.editorContentStyle,
      plugins: 'link autoresize image lists table',
      table_appearance_options: false,
      resize: true,
      // For browser native spellchecking
      browser_spellcheck: true,
      contextmenu: false,
      toolbar: 'undo redo | styleselect | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | numlist bullist ' +
        'outdent indent | link image | forecolor backcolor | table tabledelete | tableprops tablerowprops tablecellprops | ' +
        'tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
      menubar: 'edit insert view format table tools',
      // Open embedded links in a new tab so users don't lose their place in RMS (RA-2042)
      default_link_target: '_blank',
      link_default_target: '_blank',
      link_assume_external_targets: true,
      paste_remove_styles_if_webkit: false,
      images_upload_handler: async (blobInfo, progress) => {
        const formData = new FormData();
        const tokenInfo = this.localStorageService.get('tokenInfo');
        formData.append('file', blobInfo.blob(), blobInfo.filename());
        const httpOptions = {
          headers: new HttpHeaders({
            'Accept': 'application/json',
            'Authorization': tokenInfo.tokenType + ' ' + tokenInfo.accessToken
          })
        };
        return new Promise(async (resolve, reject) => {
          this.http.post('/api/rms/attachments', formData, httpOptions)
            .pipe(catchError(error => observableThrowError(error)))
            .subscribe((data: any) => {
                const json = data;
                json.location = `${window.location.origin}/api/rms/attachments/${json.id}?access_token=${tokenInfo.accessToken}`;
                if (!json || typeof json.location !== 'string') {
                  reject('The image cant be uploaded, please review the format and the size.' );
                  return;
                }
                resolve(json.location);
                // This is a modification as part of the pen test ticket. (https://racapital.atlassian.net/browse/RRP-4555)
                // We cannot display the access token in the UI, hence removing the source element in the general tab
                // because it is dynamically created at runtime by TinyMce and we have no control on the library
               setTimeout(() => {
                 $('.tox-form .tox-form__group').first().css({'display': 'none'});
               });
              },
              error => {
                this.bannerNotificationService.error(error);
                reject('The image can\'t be uploaded, please review the format and the size.' );
                return;
              });
        });
      },
      skin_url: '../../../assets/third-parties/tinymce/skins/oxide',
      content_css: '../../../assets/third-parties/tinymce/skins/oxide/content.min.css',
      promotion: false,
      min_height: 200,
      setup: editor => {
        this.editor = editor;
        editor.on('init', () => {
          this.content = this.content || '';
          editor.setContent(this.content);
          if (this.focusOnLoad) {
            editor.focus();
          }
        });
        this.editor.on('Change', () => {
          const content = this.preventExternalImage(this.editor);
          this.onEditorChange.emit(content);
        });
        this.editor.on('keyup', () => {
          this.onEditorUpdated();
        });
      }
    });
    this.connectionSubscription = this.connectionService.monitor().subscribe(value => this.isOffline = !value);
  }

  /**
   * Destroys the editor instance on destroy the component
   */
  ngOnDestroy (): void {
    tinymce.remove(this.editor);
    if (this.continuousTypingSubscription) {
      this.continuousTypingSubscription.unsubscribe();
    }
    if (this.idleTypingSubscription) {
      this.idleTypingSubscription.unsubscribe();
    }
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
    }
  }

  preventExternalImage(editor: any): string {
    const content = editor.getContent();
    // A regex that will match any img elements except those pointing to an RMS src
    const imgSrcRegex = /<img\s+[^>]*src=(['"])http(?![^'"]*api\/rms[^'"]*)[^'"]*\1[^>]*>/g;

    if (imgSrcRegex.test(content)) {
      editor.setContent(content.replace(imgSrcRegex, ''));
      this.bannerNotificationService.error(
        'Cannot insert images from external websites. Please upload the image using the Upload button.',
        '',
        5);
    }

    return editor.getContent();
  }

  /**
   * Creates 2 intervals, #1 for non-stop typing during 30 seconds and #2 for when the user stops typing for 3 seconds
   * Both intervals emits an event with the editor content so the parent can auto save the draft
   */
  private onEditorUpdated (): void {
    if (!this.continuousTypingSubscription || this.continuousTypingSubscription.closed) {
      this.continuousTypingSubscription = interval(1000).subscribe(() => {
        this.continuousTypingCounter++;
        if (this.continuousTypingCounter >= 30) {
          this.saveContent();
        }
      });
    }
    if (this.idleTypingSubscription) {
      this.idleTypingSubscription.unsubscribe();
      this.idleTypingCounter = 0;
    }
    this.idleTypingSubscription = interval(1000).subscribe(() => {
      this.idleTypingCounter++;
      if (this.idleTypingCounter >= 3) {
        this.saveContent();
      }
    });
  }

  /**
   * Emits an event Output to auto save the content
   * Restarts the timers and unsubscribes
   */
  private saveContent (): void {
    if (this.continuousTypingSubscription) {
      this.continuousTypingSubscription.unsubscribe();
      this.continuousTypingCounter = 0;
    }
    if (this.idleTypingSubscription) {
      this.idleTypingSubscription.unsubscribe();
      this.idleTypingCounter = 0;
    }
    this.autoSave.emit({content: this.editor.getContent(), isOffline: this.isOffline});
  }

  get editorContentStyle(): string {
    return `${TINYMCE_FONT_STYLE}

          /* CSS reset to prevent browser default styles */
          html, div, span, applet, object, iframe,
          h1, h2, h3, h4, h5, h6, p, blockquote, pre,
          a, abbr, acronym, address, big, cite, code,
          del, dfn, em, img, ins, kbd, q, s, samp,
          small, strike, strong, sub, sup, tt, var,
          b, u, i, center,
          dl, dt, dd, ol, ul, li,
          fieldset, form, label, legend,
          table, caption, tbody, tfoot, thead, tr, th, td,
          article, aside, canvas, details, embed,
          figure, figcaption, footer, header, hgroup,
          menu, nav, output, ruby, section, summary,
          time, mark, audio, video {
            margin: 0;
            border: 0;
            vertical-align: baseline;
          }

          body {
            font-size: 10pt !important;
            font-family: "Conv_Gotham-Book", "Helvetica Neue", sans-serif !important;
          }
          strong, b {font-family: "Conv_Gotham-Bold", "Helvetica Neue", sans-serif !important;}`;
  }

}
